const REGISTRATION_NUMBER_KIND_BY_ENTITY_TYPE = {
    INDIVIDUAL: 'ID_NUMBER',
    PTY_LTD: 'CIPC',
    INC: 'CIPC',
    CC: 'CIPC',
    FOREIGN_COMPANY: 'CIPC',
    NPC: 'CIPC',
    TRUST: 'TRUST_REFERENCE',
    BODY_CORPORATE: 'SCHEME_NUMBER',
    DECEASED_ESTATE: 'MASTERS_REFERENCE',
    PARTNERSHIP: 'NONE',
    VOLUNTARY_ASSOCIATION: 'NONE',
};
export function registrationNumberKind(entityType) {
    return REGISTRATION_NUMBER_KIND_BY_ENTITY_TYPE[entityType];
}
export function registrationDateMeaning(kind) {
    if (kind === 'ID_NUMBER')
        return 'DATE_OF_BIRTH';
    if (kind === 'MASTERS_REFERENCE')
        return 'DATE_OF_DEATH';
    if (kind === 'NONE')
        return 'NONE';
    return 'REGISTRATION_DATE';
}
/**
 * Whether a `RegistrationNumberKind` identifies the entity itself as a
 * registered organisation (CIPC, a trust, a scheme, an estate), as opposed to
 * identifying a natural person (`ID_NUMBER`) or nothing (`NONE`).
 */
export function isOrganisationalIdentifier(kind) {
    return kind !== 'ID_NUMBER' && kind !== 'NONE';
}
/**
 * Which of `registrationNumber` / `registrationDate` lose their meaning when
 * an entity's type changes from `from` to `to`. Both are `false` when either
 * type is `null` (an unrecorded type carries no meaning to compare against)
 * or the two types are equal.
 */
export function identifierMeaningChanges(from, to) {
    if (from === null || to === null || from === to) {
        return { registrationNumber: false, registrationDate: false };
    }
    const fromKind = registrationNumberKind(from);
    const toKind = registrationNumberKind(to);
    return {
        registrationNumber: fromKind !== toKind,
        registrationDate: registrationDateMeaning(fromKind) !== registrationDateMeaning(toKind),
    };
}
/**
 * Whether a supplied identifier value reads as an echo of the value already
 * stored, rather than a genuinely new one.
 *
 * Single-sourced because two call sites need the same answer and drifted
 * once already: the backend's `reconcileIdentifiersOnTypeChange` uses it to
 * decide what a type-change write actually clears, and the ledger CLI's
 * `predictedIdentifierClears` uses it to predict that clear in a dry-run
 * plan before `--execute`. Both of Bill's own edit forms echo the entity's
 * stored `registrationNumber` back in the same request body as a type
 * change, so "a value was supplied" alone is never enough to tell a
 * deliberate correction apart from an untouched form field re-submitting
 * what was already there.
 *
 * `undefined` (the field is absent from the write) and an all-whitespace
 * value both read as "nothing new was provided", the same as an explicit
 * echo. Both `stored` and `supplied` are trimmed before comparison, so a
 * copy-paste echo padded with stray leading/trailing whitespace still reads
 * as an echo. `null` (an explicit clear) is NOT an echo - the caller is
 * expected to handle that case itself, because "the operator explicitly
 * cleared this field" and "the type change cleared this field" are
 * different facts a caller may need to tell apart (the CLI's dry-run plan
 * lists an explicit `null` as the operator's own change, and would double
 * itself up if it also counted it as a type-change clear).
 */
export function identifierValueEchoesStored(stored, supplied) {
    if (supplied === null)
        return false;
    const trimmedSupplied = supplied?.trim() ?? '';
    const trimmedStored = stored?.trim() ?? '';
    return trimmedSupplied === '' || trimmedSupplied === trimmedStored;
}
